import { clsx } from "clsx";
import React, { type JSX } from "react";

import type {
  TitleIconSettingsType,
  TitleWithIconType,
} from "@shared/content-types/types";
import type { ReplacementSettingsObject } from "@shared/types";

import { BrandIcon } from "../brand-icon";

import s from "./typography.module.scss";

type PositionedIconMap = {
  [key: number]: JSX.Element;
};

type ApplyReplacementsProps = {
  text: string;
  settings: ReplacementSettingsObject;
  className?: string;
};

export const ApplyReplacements = ({
  text,
  settings,
  className,
}: ApplyReplacementsProps) => {
  const wordsAndSpaces: React.ReactNode[] = [];
  let globalIndex = 0;

  const parts = text.split(/(\s+)/);

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const isWhitespace = part.match(/\s+/);

    if (isWhitespace) {
      wordsAndSpaces.push(
        <span key={`whitespace-${globalIndex}`}>&nbsp;</span>,
      );
      globalIndex += part.length;
      continue;
    }

    const wordChars: React.ReactNode[] = [];
    const chars = part.split("");

    for (let j = 0; j < chars.length; j++) {
      const charIndex = globalIndex + j;

      if (Object.hasOwn(settings, charIndex)) {
        let replacement = settings[charIndex];

        if (React.isValidElement(replacement)) {
          replacement = React.cloneElement(replacement as React.ReactElement, {
            key: `repl-${charIndex}`,
          });
        }
        // Буква остаётся в тексте заголовка: иконка её только закрывает визуально.
        wordChars.push(
          <span key={`repl-${charIndex}`} className={s.replaced}>
            <span className={s.replacedChar}>{chars[j]}</span>
            {replacement}
          </span>,
        );
      } else {
        wordChars.push(
          <span className={s.char} key={`char-${charIndex}-${chars[j]}`}>
            {chars[j]}
          </span>,
        );
      }
    }

    wordsAndSpaces.push(
      <span key={`word-${globalIndex}-${chars[0]}`} className={s.word}>
        {wordChars}
      </span>,
    );

    globalIndex += part.length;
  }

  return (
    <span className={clsx(s.textWrapper, className)}>{wordsAndSpaces}</span>
  );
};

export const mapItemsToPositionedIcons = (
  items: TitleIconSettingsType[] | undefined | null,
): PositionedIconMap => {
  if (!items?.length) return {};
  return items.reduce((acc, item) => {
    if (item?.position == null || !item?.icon?.icon?.name) return acc;
    const key = item.position - 1;

    const iconElement = (
      <BrandIcon
        key={`icon-${item.id}-${item.position}`}
        as="span"
        aria-hidden
        name={item.icon.icon.name}
        fillColor={item.icon.fillColor}
        strokeColor={item.icon.strokeColor}
        svg_code={item.icon?.uicon?.svg_code}
        className={s.icon}
      />
    );

    acc[key] = iconElement;

    return acc;
  }, {} as PositionedIconMap);
};

export const getTitleWithIconData = (data: TitleWithIconType) => {
  return {
    text: data?.text || "",
    settings: data?.titleIconSettings
      ? mapItemsToPositionedIcons(data.titleIconSettings)
      : {},
  };
};
